import { FC } from 'react';
import { Card, HStack, Text } from '@wegroup/design-system';
import TableCheckbox from '@parties/src/features/overview/components/table/TableCheckbox';
import { useAnvaFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/anvaFlowsConfigureStore';
import { AnvaFlowsGroupedCoverage } from '@flows/src/api/anva-flows/anva-config/getAnvaFlowsCoverages';

interface Props {
  groupedCoverage: AnvaFlowsGroupedCoverage;
  insuranceId: string;
  clearError?: () => void;
  isError?: boolean;
  span?: boolean;
}

const FlowsConfigureCoverageItem: FC<Props> = ({
  insuranceId,
  groupedCoverage,
  clearError,
  isError,
  span,
}) => {
  const setCoverages = useAnvaFlowsConfigureStore(
    (state) => state.setCoverages,
  );

  // Check if ALL coverage codes from this group are selected
  const selectedCoverages = useAnvaFlowsConfigureStore(
    (state) => state.coverages[insuranceId] || [],
  );

  const isSelected = groupedCoverage.coverage_codes.every((code) =>
    selectedCoverages.includes(code),
  );

  const isPartiallySelected =
    groupedCoverage.coverage_codes.some((code) =>
      selectedCoverages.includes(code),
    ) && !isSelected;

  const isMandatory = groupedCoverage.mandatory;

  const handleSelect = () => {
    // Don't allow interaction with mandatory coverages
    if (isMandatory) return;

    if (isError) clearError?.();

    if (isSelected) {
      // Remove all coverage codes from this group
      const updatedCoverages = selectedCoverages.filter(
        (code) => !groupedCoverage.coverage_codes.includes(code),
      );
      setCoverages(insuranceId, updatedCoverages);
    } else {
      // Add all coverage codes from this group
      const newCoverages = [...selectedCoverages];
      groupedCoverage.coverage_codes.forEach((code) => {
        if (!newCoverages.includes(code)) {
          newCoverages.push(code);
        }
      });
      setCoverages(insuranceId, newCoverages);
      clearError?.();
    }
  };

  // No-op function for disabled state
  const handleNoOp = () => {
    // Intentionally empty for disabled/mandatory items
  };

  // No-op function for TableCheckbox onChange
  const handleCheckboxNoOp = (_value: boolean) => {
    // Intentionally empty for disabled/mandatory items
  };

  // Determine border color based on selection state
  let borderColor = 'gray.50';
  if (isError) {
    borderColor = 'danger.main';
  } else if (isSelected) {
    borderColor = isMandatory ? 'gray.400' : 'primary.500';
  } else if (isPartiallySelected) {
    borderColor = 'warning.500';
  }

  // Define card props based on mandatory state
  const cardProps = {
    as: 'button' as const,
    p: '4' as const,
    borderColor,
    h: '100%' as const,
    onClick: isMandatory ? handleNoOp : handleSelect,
    cursor: isMandatory ? ('not-allowed' as const) : ('pointer' as const),
    transition: '0.1s' as const,
    opacity: isMandatory ? 0.7 : 1,
    _hover: isMandatory
      ? {}
      : {
          borderColor: isSelected ? undefined : 'gray.100',
          boxShadow: 'level2',
        },
  };

  const nameColor = isMandatory ? 'gray.600' : undefined;

  return (
    <Card {...cardProps}>
      <HStack spacing="4">
        <Text flexGrow="1" color={nameColor}>
          {groupedCoverage.name}
        </Text>
        <TableCheckbox
          isChecked={isMandatory ? true : isSelected}
          isDisabled={isMandatory}
          onChange={isMandatory ? handleCheckboxNoOp : handleSelect}
        />
      </HStack>
    </Card>
  );
};

export default FlowsConfigureCoverageItem;
